// src/pages/AdminPage.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useApi } from '../hooks/useApi';
import { 
  Shield, 
  Users, 
  FileText, 
  Settings2, 
  Bell, 
  Zap, 
  ArrowRight,
  Database,
  Lock,
  ArrowLeft,
  X,
  Plus,
  Trash2,
  Save,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  Clock,
  Briefcase
} from 'lucide-react';

/* ── Animation Variants ──────────────────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.08 } },
};

/* ── UI Components ───────────────────────────────────────── */
function SystemCard({ label, value, subtext, onClick }) {
  return (
    <motion.div
      variants={fadeUp}
      whileHover={{ y: -4 }}
      onClick={onClick}
      style={{
        background: 'var(--surface)',
        padding: '24px',
        borderRadius: '16px',
        border: '1px solid var(--white-05)',
        backdropFilter: 'blur(16px)',
        cursor: onClick ? 'pointer' : 'default',
        flex: 1
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>
        {label}
      </div>
      <div style={{ fontSize: 32, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1 }}>
        {value}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 8 }}>{subtext}</div>
    </motion.div>
  );
}

function ActionCard({ title, desc, buttonText, onClick, icon: Icon }) {
  return (
    <motion.div
      variants={fadeUp}
      whileHover={{ y: -2 }}
      style={{
        background: 'var(--surface)',
        padding: '24px',
        borderRadius: '16px',
        border: '1px solid var(--white-05)',
        backdropFilter: 'blur(16px)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}
    >
      <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
        <div style={{ background: 'var(--black-30)', padding: 12, borderRadius: 10, color: 'var(--text-muted)' }}>
          <Icon size={24} strokeWidth={1.5} />
        </div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{desc}</div>
        </div>
      </div>
      <button 
        onClick={onClick}
        style={{
          background: (buttonText === 'Configure' || buttonText === 'Manage Users' || buttonText === 'View Analytics') ? 'var(--brand-gradient)' : 'transparent',
          color: (buttonText === 'Configure' || buttonText === 'Manage Users' || buttonText === 'View Analytics') ? '#000' : 'var(--text-secondary)',
          border: (buttonText === 'Configure' || buttonText === 'Manage Users' || buttonText === 'View Analytics') ? 'none' : '1px solid var(--white-06)',
          padding: '8px 16px',
          borderRadius: '8px',
          fontSize: 13,
          fontWeight: 700,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          gap: 6
        }}
      >
        {buttonText} <ArrowRight size={14} />
      </button>
    </motion.div>
  );
}

function ControlItem({ title, desc, icon: Icon }) {
  return (
    <motion.div
      whileHover={{ y: -2 }}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '16px',
        border: '1px solid var(--white-05)',
        borderRadius: '16px',
        background: 'var(--surface)',
        cursor: 'pointer',
        gap: '16px',
        backdropFilter: 'blur(16px)',
        transition: 'all 0.2s ease'
      }}
    >
      <div style={{ background: 'var(--black-30)', padding: '10px', borderRadius: '10px', color: 'var(--text-muted)' }}>
        <Icon size={20} strokeWidth={1.5} />
      </div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{desc}</div>
      </div>
    </motion.div>
  );
}

/* ── CONFIG PANELS ───────────────────────────────────────── */
function Modal({ title, isOpen, onClose, children }) {
  if (!isOpen) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--black-70)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        style={{ background: 'var(--dark-modal)', width: '100%', maxWidth: 640, borderRadius: 20, border: '1px solid var(--white-06)', boxShadow: '0 25px 50px -12px var(--black-50)', overflow: 'hidden', backdropFilter: 'blur(20px)' }}
      >
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--white-05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: 'var(--text-primary)' }}>{title}</h2>
          <button onClick={onClose} style={{ color: 'var(--text-muted)', cursor: 'pointer', border: 'none', background: 'none' }}><X size={20} /></button>
        </div>
        <div style={{ padding: 24, maxHeight: '70vh', overflowY: 'auto' }}>{children}</div>
      </motion.div>
    </div>
  );
}

function TicketSettingsPanel({ data, onSave, showToast }) {
  const [priorities, setPriorities] = useState(data.priorities || []);
  const [bizHours, setBizHours] = useState(data.settings?.business_hours || { enabled: false, start: '09:00', end: '18:00' });

  const handlePrioChange = (id, field, val) => {
    setPriorities(prev => prev.map(p => p.id === id ? { ...p, [field]: parseInt(val) || 0 } : p));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <section>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Clock size={16} color="var(--text-64748b)" />
          <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--text-secondary)' }}>Priority & SLA Configuration</h4>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {priorities.map(p => (
            <div key={p.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, padding: 12, background: 'var(--black-20)', borderRadius: 10, border: '1px solid var(--white-04)', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: p.color }} />
                <span style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</span>
              </div>
              <div>
                <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Response (min)</label>
                <input type="number" value={p.response_sla_minutes} onChange={e => handlePrioChange(p.id, 'response_sla_minutes', e.target.value)} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--white-06)', background: 'var(--black-30)', color: 'var(--text-primary)', fontSize: 13, outline: 'none' }} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Resolution (min)</label>
                <input type="number" value={p.resolution_sla_minutes} onChange={e => handlePrioChange(p.id, 'resolution_sla_minutes', e.target.value)} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--white-06)', background: 'var(--black-30)', color: 'var(--text-primary)', fontSize: 13, outline: 'none' }} />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Briefcase size={16} color="var(--text-64748b)" />
          <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--text-secondary)' }}>Business Hours</h4>
        </div>
        <div style={{ padding: 16, background: 'var(--black-20)', borderRadius: 10, border: '1px solid var(--white-04)' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: bizHours.enabled ? 16 : 0 }}>
            <input type="checkbox" checked={bizHours.enabled} onChange={e => setBizHours(p => ({ ...p, enabled: e.target.checked }))} />
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Enforce business hours for SLA</span>
          </label>
          {bizHours.enabled && (
            <div style={{ display: 'flex', gap: 20 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Start Time</label>
                <input type="time" value={bizHours.start} onChange={e => setBizHours(p => ({ ...p, start: e.target.value }))} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--white-06)', background: 'var(--black-30)', color: 'var(--text-primary)', outline: 'none' }} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>End Time</label>
                <input type="time" value={bizHours.end} onChange={e => setBizHours(p => ({ ...p, end: e.target.value }))} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--white-06)', background: 'var(--black-30)', color: 'var(--text-primary)', outline: 'none' }} />
              </div>
            </div>
          )}
        </div>
      </section>

      <button onClick={() => onSave({ priorities, business_hours: bizHours })} style={{ background: 'var(--brand-gradient)', color: '#000', border: 'none', padding: '12px', borderRadius: 10, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'all 0.2s', boxShadow: '0 0 15px rgba(96, 165, 250, 0.3)' }}>
        <Save size={16} /> Save Ticket Configuration
      </button>
    </div>
  );
}

function NotificationSettingsPanel({ data, onSave, showToast }) {
  const { request } = useApi();
  const [smtp, setSmtp] = useState(data.smtp || { host: '', port: 587, user: '', pass: '********', secure: false, enabled: true });
  const [imap, setImap] = useState(data.imap || { protocol: 'IMAP', host: '', port: 993, user: '', pass: '********', encryption: 'SSL', interval: '5', auto_ticket: true, enabled: false });
  const [channels, setChannels] = useState(data.channels || { email_new: true, email_assign: true, email_status: true, slack_enabled: false });

  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--white-06)', background: 'var(--black-30)', color: 'var(--text-primary)', boxSizing: 'border-box', outline: 'none', fontSize: 13 };
  const labelStyle = { fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 6, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' };
  const sectionStyle = { padding: 20, background: 'var(--white-03)', borderRadius: 12, border: '1px solid var(--white-04)', marginBottom: 20 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {/* 📤 Outgoing Mail (SMTP) */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Outgoing Mail (SMTP)</h4>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>Used for sending notifications</p>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: smtp.enabled ? '#059669' : 'var(--text-muted)' }}>{smtp.enabled ? 'Enabled' : 'Disabled'}</span>
            <input type="checkbox" checked={smtp.enabled} onChange={e => setSmtp(p => ({ ...p, enabled: e.target.checked }))} />
          </label>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, opacity: smtp.enabled ? 1 : 0.5, pointerEvents: smtp.enabled ? 'auto' : 'none' }}>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={labelStyle}>SMTP Host</label>
            <input value={smtp.host} onChange={e => setSmtp(p => ({ ...p, host: e.target.value }))} style={inputStyle} placeholder="smtp.gmail.com" />
          </div>
          <div>
            <label style={labelStyle}>Port</label>
            <input type="number" value={smtp.port} onChange={e => setSmtp(p => ({ ...p, port: e.target.value }))} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Encryption</label>
            <select value={smtp.secure ? 'ssl' : 'tls'} onChange={e => setSmtp(p => ({ ...p, secure: e.target.value === 'ssl' }))} style={{ ...inputStyle, height: 38 }}>
              <option value="tls">STARTTLS / Port 587</option>
              <option value="ssl">SSL / Port 465</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Username</label>
            <input value={smtp.user} onChange={e => setSmtp(p => ({ ...p, user: e.target.value }))} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Password</label>
            <input type="password" value={smtp.pass} onChange={e => setSmtp(p => ({ ...p, pass: e.target.value }))} style={inputStyle} />
          </div>
        </div>
        <button 
          onClick={() => request('/api/admin/smtp-test', { method: 'POST', body: JSON.stringify(smtp) }).then(() => showToast('SMTP connection successful', 'success')).catch(e => showToast(e.message, 'error'))} 
          disabled={!smtp.enabled}
          style={{ width: '100%', marginTop: 16, padding: '10px', borderRadius: 8, border: '1px solid var(--white-06)', background: 'var(--white-05)', color: 'var(--text-primary)', fontWeight: 600, cursor: 'pointer', fontSize: 13, opacity: smtp.enabled ? 1 : 0.5, transition: 'all 0.2s' }}
        >
          Test SMTP Connection
        </button>
      </div>

      {/* 📥 Incoming Mail (IMAP/POP3) */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Incoming Mail (IMAP/POP3)</h4>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>Used for receiving and creating tickets</p>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: imap.enabled ? '#059669' : 'var(--text-muted)' }}>{imap.enabled ? 'Enabled' : 'Disabled'}</span>
            <input type="checkbox" checked={imap.enabled} onChange={e => setImap(p => ({ ...p, enabled: e.target.checked }))} />
          </label>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, opacity: imap.enabled ? 1 : 0.5, pointerEvents: imap.enabled ? 'auto' : 'none' }}>
          <div>
            <label style={labelStyle}>Protocol</label>
            <select value={imap.protocol} onChange={e => setImap(p => ({ ...p, protocol: e.target.value }))} style={{ ...inputStyle, height: 38 }}>
              <option value="IMAP">IMAP</option>
              <option value="POP3">POP3</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Check Interval</label>
            <select value={imap.interval} onChange={e => setImap(p => ({ ...p, interval: e.target.value }))} style={{ ...inputStyle, height: 38 }}>
              <option value="1">Every 1 minute</option>
              <option value="5">Every 5 minutes</option>
              <option value="15">Every 15 minutes</option>
            </select>
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={labelStyle}>Mail Server Host</label>
            <input value={imap.host} onChange={e => setImap(p => ({ ...p, host: e.target.value }))} style={inputStyle} placeholder="imap.gmail.com" />
          </div>
          <div>
            <label style={labelStyle}>Port</label>
            <input type="number" value={imap.port} onChange={e => setImap(p => ({ ...p, port: e.target.value }))} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Encryption</label>
            <select value={imap.encryption} onChange={e => setImap(p => ({ ...p, encryption: e.target.value }))} style={{ ...inputStyle, height: 38 }}>
              <option value="SSL">SSL / TLS</option>
              <option value="STARTTLS">STARTTLS</option>
              <option value="None">None</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Username</label>
            <input value={imap.user} onChange={e => setImap(p => ({ ...p, user: e.target.value }))} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Password</label>
            <input type="password" value={imap.pass} onChange={e => setImap(p => ({ ...p, pass: e.target.value }))} style={inputStyle} />
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '14px', background: 'var(--black-20)', borderRadius: 10, border: '1px solid var(--white-04)' }}>
              <input type="checkbox" checked={imap.auto_ticket} onChange={e => setImap(p => ({ ...p, auto_ticket: e.target.checked }))} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Auto Create Ticket</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Incoming emails automatically become tickets</div>
              </div>
            </label>
          </div>
        </div>
        <button 
          onClick={() => request('/api/admin/imap-test', { method: 'POST', body: JSON.stringify(imap) }).then(() => showToast('IMAP connection successful', 'success')).catch(e => showToast(e.message, 'error'))} 
          disabled={!imap.enabled}
          style={{ width: '100%', marginTop: 16, padding: '10px', borderRadius: 8, border: '1px solid var(--white-06)', background: 'var(--white-05)', color: 'var(--text-primary)', fontWeight: 600, cursor: 'pointer', fontSize: 13, opacity: imap.enabled ? 1 : 0.5, transition: 'all 0.2s' }}
        >
          Test Incoming Connection
        </button>
      </div>

      <button onClick={() => onSave({ smtp, imap, channels })} style={{ width: '100%', background: 'var(--brand-gradient)', color: '#000', border: 'none', padding: '14px', borderRadius: 10, fontWeight: 800, cursor: 'pointer', fontSize: 14, transition: 'all 0.2s', boxShadow: '0 0 15px rgba(96, 165, 250, 0.3)' }}>
        Save All Notification Settings
      </button>
    </div>
  );
}

function AutomationSettingsPanel({ data, onSave, showToast }) {
  const [rules, setRules] = useState(data.rules || []);
  
  const addRule = () => setRules([...rules, { id: Date.now(), name: 'New Rule', trigger_type: 'created', conditions_json: {}, action_json: {}, is_active: true }]);
  const deleteRule = (id) => setRules(rules.filter(r => r.id !== id));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {rules.map(rule => (
        <div key={rule.id} style={{ padding: 16, background: 'var(--black-20)', borderRadius: 12, border: '1px solid var(--white-04)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <input value={rule.name} onChange={e => setRules(prev => prev.map(r => r.id === rule.id ? { ...r, name: e.target.value } : r))} style={{ background: 'none', border: 'none', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', padding: 0 }} />
            <button onClick={() => deleteRule(rule.id)} style={{ color: '#EF4444', cursor: 'pointer', background: 'none', border: 'none' }}><Trash2 size={16} /></button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Trigger</label>
              <select value={rule.trigger_type} onChange={e => setRules(prev => prev.map(r => r.id === rule.id ? { ...r, trigger_type: e.target.value } : r))} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--white-06)', background: 'var(--black-30)', color: 'var(--text-primary)', fontSize: 13, outline: 'none' }}>
                <option value="created">On Ticket Creation</option>
                <option value="assigned">On Ticket Assigned</option>
                <option value="status_changed">On Status Change</option>
                <option value="stale">After 24h Inactivity</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Action</label>
              <select value={rule.action_json.type || ''} onChange={e => setRules(prev => prev.map(r => r.id === rule.id ? { ...r, action_json: { type: e.target.value } } : r))} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--white-06)', background: 'var(--black-30)', color: 'var(--text-primary)', fontSize: 13, outline: 'none' }}>
                <option value="">Select Action...</option>
                <option value="auto_assign">Auto-Assign to Team</option>
                <option value="set_priority">Set Priority to Critical</option>
                <option value="send_alert">Send Urgent Alert</option>
              </select>
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={rule.is_active} onChange={e => setRules(prev => prev.map(r => r.id === rule.id ? { ...r, is_active: e.target.checked } : r))} />
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>Active</span>
          </label>
        </div>
      ))}
      <button onClick={addRule} style={{ border: '1px dashed var(--white-20)', padding: 12, borderRadius: 12, color: 'var(--text-muted)', fontSize: 13, fontWeight: 600, background: 'var(--white-03)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, transition: 'all 0.2s' }}>
        <Plus size={16} /> Add Automation Rule
      </button>
      <button onClick={() => onSave({ rules })} style={{ background: 'var(--brand-gradient)', color: '#000', border: 'none', padding: '12px', borderRadius: 10, fontWeight: 800, cursor: 'pointer', marginTop: 10, transition: 'all 0.2s', boxShadow: '0 0 15px rgba(96, 165, 250, 0.3)' }}>Save Workflow Rules</button>
    </div>
  );
}

export default function AdminPage({ showToast }) {
  const [data, setData] = useState(null);
  const [configPanel, setConfigPanel] = useState(null); // 'tickets', 'notifications', 'automation'
  const { request, loading } = useApi();
  const navigate = useNavigate();

  useEffect(() => {
    request('/api/admin/control-center')
      .then(setData)
      .catch(err => {
        // Fallback for visual testing if api not yet ready
        setData({
          stats: { total_tickets: 0, closed_tickets: 0 },
          ai_resolved_count: 0,
          priorities: [
            { id: 1, name: 'Low', response_sla_minutes: 240, resolution_sla_minutes: 1440, color: 'var(--text-muted)' },
            { id: 2, name: 'Medium', response_sla_minutes: 120, resolution_sla_minutes: 720, color: 'var(--text-muted)' },
            { id: 3, name: 'High', response_sla_minutes: 60, resolution_sla_minutes: 240, color: 'var(--text-primary)' },
            { id: 4, name: 'Critical', response_sla_minutes: 15, resolution_sla_minutes: 60, color: 'var(--text-primary)' }
          ],
          settings: { business_hours: { enabled: true, start: '08:00', end: '20:00' } },
          smtp: { host: 'smtp.beo-helpdesk.com', port: 587, user: 'mailer@beo.in' },
          channels: { email_new: true, email_assign: true, email_status: true, slack_enabled: false },
          rules: []
        });
      });
  }, [request, showToast]);

  const handleSave = (payload) => {
    request('/api/admin/control-center', { method: 'POST', body: JSON.stringify(payload) })
      .then(() => {
        showToast('Settings saved successfully', 'success');
        setConfigPanel(null);
      })
      .catch(err => showToast(err.message, 'error'));
  };

  if (loading && !data) return <div className="p-12"><div className="spinner lg mx-auto"></div></div>;
  if (!data) return null;

  const { stats, ai_resolved_count } = data || {};

  return (
    <motion.div 
      initial="hidden"
      animate="visible"
      variants={stagger}
      style={{ width: '100%', boxSizing: 'border-box', background: 'transparent', minHeight: '100vh', padding: '24px' }}
    >
      {/* ── HEADER ────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
            Platform Settings
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--text-muted)' }}>
            Manage core system configurations and user access levels.
          </p>
        </div>
        <button 
          onClick={() => navigate('/dashboard')}
          style={{
            background: 'var(--white-05)', border: '1px solid var(--white-05)', borderRadius: '10px',
            padding: '8px 16px', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)',
            display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', transition: 'all 0.2s'
          }}
        >
          <ArrowLeft size={14} /> Back to Dashboard
        </button>
      </div>

      {/* ── SECTION 1: SYSTEM OVERVIEW ────────────────────── */}
      <div style={{ marginBottom: 40 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 16 }}>
          System Overview
        </h3>
        <div style={{ display: 'flex', gap: 16 }}>
          <SystemCard 
            label="AI Resolved Issues" 
            value={ai_resolved_count || 0} 
            subtext="Auto-resolved tickets" 
            onClick={() => navigate('/admin/ai-resolved')}
          />
          <SystemCard 
            label="Total Tickets" 
            value={stats.total_tickets || 0} 
            subtext="All-time tickets" 
            onClick={() => navigate('/tickets?filter=all')}
          />
          <SystemCard 
            label="Total Closed" 
            value={stats.closed_tickets || 0} 
            subtext="Successfully resolved" 
            onClick={() => navigate('/tickets?filter=closed')}
          />
        </div>
      </div>

      {/* ── SECTION 2 / 3 GRID ────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 32 }}>
        
        {/* LEFT: ACCESS & AUTHENTICATION */}
        <div>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 16 }}>
            Access & Authentication
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <ActionCard 
              title="Directory Authentication" 
              desc="Manage LDAP / AD integration" 
              buttonText="Configure" 
              icon={Database}
              onClick={() => navigate('/admin/ldap')}
            />
            <ActionCard 
              title="Roles & Permissions" 
              desc="Control user access levels" 
              buttonText="Manage Users" 
              icon={Users}
              onClick={() => navigate('/admin/users')}
            />
            <ActionCard 
              title="Team Performance" 
              desc="Monitor IT productivity & SLA trends" 
              buttonText="View Analytics" 
              icon={Zap}
              onClick={() => navigate('/admin/performance')}
            />
          </div>
        </div>

        {/* RIGHT: SYSTEM CONTROLS */}
        <div>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 16 }}>
            System Controls
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div onClick={() => setConfigPanel('tickets')}>
              <ControlItem title="Ticket Settings" desc="Configure priorities, SLA rules" icon={Settings2} />
            </div>
            <div onClick={() => setConfigPanel('notifications')}>
              <ControlItem title="Notification Settings" desc="Email / alert configurations" icon={Bell} />
            </div>
            <div onClick={() => setConfigPanel('automation')}>
              <ControlItem title="Automation Rules" desc="Auto-assign, escalation rules" icon={Zap} />
            </div>
          </div>
        </div>

      </div>

      {/* ── MODALS ────────────────────────────────────────── */}
      <Modal 
        title="Ticket & SLA Settings" 
        isOpen={configPanel === 'tickets'} 
        onClose={() => setConfigPanel(null)}
      >
        <TicketSettingsPanel data={data} onSave={handleSave} showToast={showToast} />
      </Modal>

      <Modal 
        title="Notification Channels" 
        isOpen={configPanel === 'notifications'} 
        onClose={() => setConfigPanel(null)}
      >
        <NotificationSettingsPanel data={data} onSave={handleSave} showToast={showToast} />
      </Modal>

      <Modal 
        title="Workflow Automation" 
        isOpen={configPanel === 'automation'} 
        onClose={() => setConfigPanel(null)}
      >
        <AutomationSettingsPanel data={data} onSave={handleSave} showToast={showToast} />
      </Modal>

    </motion.div>
  );
}
